import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  PlayArrow as RunIcon,
  ErrorOutline as ErrorIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { Play } from 'lucide-react';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { Project, ProjectsResponse } from '@/types';

const POLL_INTERVAL = 8000;

const DEBOUNCE_MS = 400;

export default function TriggerExtractionPage() {
  const theme = useTheme();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [extracting, setExtracting] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [runningCount, setRunningCount] = useState(0);
  const [queuedCount, setQueuedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  // Fetch when pagination or search changes
  useEffect(() => {
    fetchProjects();
  }, [page, rowsPerPage, debouncedSearch]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (runningCount > 0 || queuedCount > 0) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [runningCount, queuedCount]);

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => fetchProjects(true), POLL_INTERVAL);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const fetchProjects = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        ...(debouncedSearch && { search: debouncedSearch }),
      });
      const res = await sentinelNetwork.get<ProjectsResponse>(`/projects?${params}`);
      const data = res.data;
      setProjects(data.projects || []);
      setTotal(data.total || 0);
      setTotalFiltered(data.totalFiltered ?? data.total ?? 0);
      setRunningCount(data.runningCount || 0);
      setQueuedCount(data.queuedCount || 0);
      setFailedCount(data.failedCount || 0);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  const handleToggle = (appId: string) => {
    const project = projects.find((p) => p.appId === appId);
    if (project?.extractionStatus === 'running') return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) next.delete(appId);
      else next.add(appId);
      return next;
    });
  };

  const handleSelectAll = () => {
    const selectable = projects.filter((p) => p.extractionStatus !== 'running');
    const allSelected = selectable.every((p) => selected.has(p.appId));
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        selectable.forEach((p) => next.delete(p.appId));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        selectable.forEach((p) => next.add(p.appId));
        return next;
      });
    }
  };

  const handleSelectUnprocessed = () => {
    const unprocessed = projects
      .filter((p) => !p.hasSentinel && p.extractionStatus !== 'running')
      .map((p) => p.appId);
    setSelected((prev) => {
      const next = new Set(prev);
      unprocessed.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExtract = async (scanType: 'delta' | 'complete' = 'delta') => {
    if (selected.size === 0) return;

    setExtracting(true);
    setMenuAnchor(null);

    try {
      const appIds = Array.from(selected);
      await sentinelNetwork.post('/extract-all', { appIds, scanType });
      setSnackbar({
        open: true,
        message: `${scanType === 'complete' ? 'Full' : 'Delta'} extraction triggered for ${appIds.length} app${appIds.length !== 1 ? 's' : ''}`,
        severity: 'success',
      });
      setSelected(new Set());
      await fetchProjects(true);
    } catch (err: any) {
      console.error('Extraction trigger failed:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to trigger extraction',
        severity: 'error',
      });
    } finally {
      setExtracting(false);
    }
  };

  const getStatusChip = (project: Project) => {
    if (project.extractionStatus === 'queued') {
      return (
        <Chip
          icon={<CircularProgress size={12} />}
          label="Queued..."
          size="small"
          color="info"
          variant="outlined"
        />
      );
    }

    if (project.extractionStatus === 'running') {
      const p = project.extractionProgress;
      const label = p && p.total > 0
        ? `${p.completed}/${p.total} questions${p.failed > 0 ? ` (${p.failed} failed)` : ''}`
        : (project.extractionStep || 'Running...');
      return (
        <Stack spacing={0.5} sx={{ minWidth: 160 }}>
          <Chip
            icon={<CircularProgress size={12} />}
            label={label}
            size="small"
            color="primary"
            variant="outlined"
          />
          <LinearProgress
            variant="indeterminate"
            sx={{ height: 3, borderRadius: 1 }}
          />
        </Stack>
      );
    }

    if (project.extractionStatus === 'failed') {
      return (
        <Tooltip title={project.extractionError || 'Unknown error'} arrow>
          <Chip
            icon={<ErrorIcon />}
            label="Failed"
            size="small"
            color="error"
            variant="outlined"
            sx={{ cursor: 'help' }}
          />
        </Tooltip>
      );
    }

    if (project.hasSentinel) {
      return <Chip label="Extracted" size="small" color="success" variant="filled" />;
    }

    return <Chip label="Pending" size="small" color="default" variant="outlined" />;
  };

  const hasRunning = runningCount > 0 || queuedCount > 0;
  const selectableOnPage = projects.filter((p) => p.extractionStatus !== 'running');
  const allOnPageSelected = selectableOnPage.length > 0 && selectableOnPage.every((p) => selected.has(p.appId));
  const someOnPageSelected = selectableOnPage.some((p) => selected.has(p.appId)) && !allOnPageSelected;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex' }}>
              <Play size={24} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h5" fontWeight={300}>
              Trigger Extraction
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
            Run insight extraction on projects to analyze their codebase.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleSelectUnprocessed}
            disabled={hasRunning}
          >
            Select Unprocessed
          </Button>
          <ButtonGroup variant="contained" disabled={selected.size === 0 || extracting || hasRunning}>
            <Button
              startIcon={extracting ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
              onClick={() => handleExtract('delta')}
            >
              {hasRunning
                ? `${runningCount + queuedCount} Running...`
                : extracting
                ? 'Triggering...'
                : `Delta Scan ${selected.size} Project${selected.size !== 1 ? 's' : ''}`}
            </Button>
            <Button
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ px: 0.5 }}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem onClick={() => handleExtract('delta')}>
              Delta Scan ({selected.size}) — only new questions
            </MenuItem>
            <MenuItem onClick={() => handleExtract('complete')}>
              Full Scan ({selected.size}) — re-analyze everything
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {hasRunning && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{runningCount + queuedCount}</strong> extraction{(runningCount + queuedCount) !== 1 ? 's' : ''} currently running.
          New triggers are disabled until all complete. Auto-refreshing every {POLL_INTERVAL / 1000}s.
        </Alert>
      )}

      {failedCount > 0 && !hasRunning && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{failedCount}</strong> extraction{failedCount !== 1 ? 's' : ''} failed.
          Hover over the "Failed" chip to see the error. You can re-select and retry.
        </Alert>
      )}

      <TextField
        placeholder="Search projects..."
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 350 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Card variant="outlined" elevation={0}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 'calc(100vh - 350px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someOnPageSelected}
                      checked={allOnPageSelected}
                      onChange={handleSelectAll}
                      disabled={hasRunning}
                    />
                  </TableCell>
                  <TableCell><strong>App ID</strong></TableCell>
                  <TableCell><strong>App Name</strong></TableCell>
                  <TableCell align="center"><strong>Repos</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell><strong>Last Extracted</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project) => {
                  const isRunning = project.extractionStatus === 'running' || project.extractionStatus === 'queued';
                  return (
                    <TableRow
                      key={project.appId}
                      hover={!isRunning}
                      onClick={() => handleToggle(project.appId)}
                      sx={{
                        cursor: isRunning ? 'default' : 'pointer',
                        opacity: isRunning ? 0.85 : 1,
                        bgcolor: isRunning ? 'action.hover' : undefined,
                      }}
                      selected={selected.has(project.appId)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.has(project.appId)}
                          disabled={isRunning || hasRunning}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize={13}>
                          {project.appId}
                        </Typography>
                      </TableCell>
                      <TableCell>{project.appName}</TableCell>
                      <TableCell align="center">{project.repoCount}</TableCell>
                      <TableCell align="center">
                        {getStatusChip(project)}
                      </TableCell>
                      <TableCell>
                        {project.extractionStatus === 'failed' && project.failedAt
                          ? `Failed ${new Date(project.failedAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}`
                          : project.extractedAt
                          ? new Date(project.extractedAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '\u2014'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {projects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {search ? 'No projects match your search' : 'No projects found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalFiltered}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>

      <Typography variant="body2" color="text.secondary" mt={1}>
        {total} total &middot;{' '}
        {runningCount} running &middot;{' '}
        {failedCount} failed
        {selected.size > 0 && (
          <> &middot; <strong>{selected.size} selected</strong></>
        )}
      </Typography>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
